import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardHeader, CardBody, Container, Row, Col, 
  Input, Button, InputGroup, InputGroupAddon
} from "reactstrap";
import api from "../../services/api"; 
import { getToken } from "../../services/auth";

const DEFAULT_MESSAGES = [
  { role: 'assistant', content: 'Olá! Sou o <b>SmartChat</b>. Como posso ajudar com os processos SAP hoje?' }
];

const SmartChatPage = () => {
  const [messages, setMessages] = useState(() => {
    const savedChat = localStorage.getItem('smartflow_chat_history');
    if (!savedChat) return DEFAULT_MESSAGES;

    try {
      const parsedChat = JSON.parse(savedChat);
      return Array.isArray(parsedChat) ? parsedChat : DEFAULT_MESSAGES;
    } catch (error) {
      localStorage.removeItem('smartflow_chat_history');
      return DEFAULT_MESSAGES;
    }
  });
  
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef(messages);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
    localStorage.setItem('smartflow_chat_history', JSON.stringify(messages));
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const clearChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    messagesRef.current = DEFAULT_MESSAGES;
    setMessages(DEFAULT_MESSAGES);
    setInput('');
    setLoading(false);
    localStorage.setItem('smartflow_chat_history', JSON.stringify(DEFAULT_MESSAGES));
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMsg = { role: 'user', content: input };
    const currentMessages = messagesRef.current;
    const updatedMessages = [...currentMessages, userMsg];
    const currentQuestion = input;
    const historyToSend = currentMessages
      .filter(message =>
        message &&
        message.content &&
        !(message.role === 'assistant' && message.content.includes('Sou o <b>SmartChat</b>'))
      )
      .slice(-10);
    const abortController = new AbortController();

    abortControllerRef.current = abortController;

    setMessages([...updatedMessages, { role: 'assistant', content: '', source: null }]);
    setInput('');
    setLoading(true);

    try {
      const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_URL || '';
      const token = getToken();
      const response = await fetch(`${baseURL}/chat/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ question: currentQuestion, history: historyToSend }),
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error('Falha na conexão com o servidor.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let accumulatedText = '';
      let source = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const raw = part.slice(6).trim();
          if (raw === '[DONE]') continue;

          try {
            const parsed = JSON.parse(raw);
            if (parsed.error) {
              accumulatedText = parsed.error;
            } else if (parsed.source !== undefined) {
              source = parsed.source;
            } else if (parsed.final) {
              accumulatedText = parsed.text ?? accumulatedText;
            } else if (parsed.text) {
              accumulatedText += parsed.text;
            }

            const nextContent = accumulatedText;
            const nextSource = source;

            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: nextContent,
                source: nextSource,
              };
              return updated;
            });
          } catch (e) {
            continue;
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: 'Erro ao conectar ao servidor do SmartFlow.',
        };
        return updated;
      });
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }

      setLoading(false);
    }
  };

  return (
    <>
      <div className="header bg-primary pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="6" xs="7">
                <h6 className="h2 text-white d-inline-block mb-0">SmartChat GoLive</h6>
              </Col>
              <Col className="text-right" lg="6" xs="5">
                <Button className="btn-neutral" color="default" onClick={clearChat} size="sm">
                  Limpar Conversa
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      <Container className="mt--6" fluid>
        <Row>
          <Col md="12">
            <Card className="shadow border-0" style={{ height: "75vh", display: "flex", flexDirection: "column" }}>
              <CardHeader className="bg-transparent border-bottom">
                <h3 className="mb-0">SmartChat</h3>
              </CardHeader>
              
              <CardBody style={{ flex: 1, overflowY: "auto", padding: "1.5rem", background: "#f6f9fc" }}>
                {messages.map((msg, i) => (
                  <div key={i} className={`d-flex mb-4 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                    <div className={`p-3 shadow-sm ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-white text-dark'}`} 
                         style={{ borderRadius: "15px", maxWidth: "80%" }}>
                      <div
                        className="smart-chat-message-content"
                        dangerouslySetInnerHTML={{ __html: msg.content }} 
                      />
                    </div>
                  </div>
                ))}
                {loading && <div className="text-muted small mb-3">
                  <i className="fa fa-circle-notch fa-spin mr-2"></i>Analisando base de conhecimento...
                </div>}
                <div ref={messagesEndRef} />
              </CardBody>

              <div className="p-4 border-top bg-white">
                <InputGroup className="input-group-merge shadow-none border-1">
                  <Input 
                    placeholder="Sua dúvida técnica..." 
                    type="text" 
                    value={input} 
                    style={{ border: "1px solid #dee2e6", borderRadius: "25px 0 0 25px", paddingLeft: "20px" }}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  />
                  <InputGroupAddon addonType="append">
                    <Button 
                      color="primary" 
                      onClick={handleSend} 
                      disabled={loading}
                      style={{ borderRadius: "0 25px 25px 0", padding: "0 25px" }}
                    >
                      {loading ? '...' : 'Enviar'}
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SmartChatPage;
